import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { useMemo, useState } from 'react'
import CopyToClipboard from 'core/components/CopyToClipboard'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Tooltip from 'core/elements/tooltip/Tooltip'
import Theme from 'core/themes/model'
import { hexToRgbaCss } from 'core/utils/colorHelpers'
import { BadgesProps } from 'k8s/components/common/entity/labels-and-annotations/model'
import { memoize } from 'utils/misc'
import generateTestId from 'utils/test-helpers'
import Button from '../button'
import { middleRight, topMiddle } from '../menu/defaults'
import TooltipListBody from '../tooltip/TooltipListBody'
import Badge from './Badge'

const defaultMaxBadgesVisible = {
  panel: 6,
  table: 3,
  string: 6,
}

const tooltipListProps = {
  ...topMiddle,
  origin: 'right bottom',
}

const getDefaultEllipsisAt = memoize((entityType) =>
  entityType === 'annotations' ? 200 : entityType === 'labels' ? 15 : null,
)

const TooltipOverlay = ({ showAll, text, classes }) => {
  const [copied, setCopied] = useState(false)
  const clickedOverlay = () => {
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 1000)
  }
  return (
    <Tooltip message={showAll ? 'Copy' : null} {...middleRight}>
      <CopyToClipboard
        copyText={text}
        copyIcon={false}
        inline={false}
        className={clsx(classes.copyToClipboard, 'copyToClipboard')}
        triggerWithChild
      >
        <div className={classes.copyContainer} onClick={clickedOverlay}>
          <FontAwesomeIcon size="md" className={clsx(classes.tooltipText, classes.tooltipCopyIcon)}>
            {copied ? 'check' : 'copy'}
          </FontAwesomeIcon>
        </div>
      </CopyToClipboard>
    </Tooltip>
  )
}

export default function Badges({
  values: allItems = [],
  entityType = 'string',
  containerType = 'panel',
  variant,
  ellipsisAt: _ellipsisAt = getDefaultEllipsisAt(entityType),
  maxVisible: _maxVisible,
  showMoreButton = false,
  bold = false,
  showCopy = true,
  useTooltipTextInOverflow = false,
}: BadgesProps) {
  const maxVisible = _maxVisible || defaultMaxBadgesVisible[entityType]
  const [showAll, setShowAll] = useState(false)
  const ellipsisAt = containerType === 'table' ? (showAll ? null : 15) : _ellipsisAt
  const classes = useStyles()

  const [itemsToShow, remainingLabels] = useMemo(() => {
    if (!showAll && allItems.length > maxVisible) {
      return [allItems.slice(0, maxVisible), allItems.slice(maxVisible)]
    }
    return [allItems, []]
  }, [showAll, allItems, maxVisible])

  const showButton = useMemo(() => {
    return showMoreButton && allItems.length > maxVisible
  }, [showMoreButton, allItems, maxVisible])
  return (
    <div
      className={clsx(classes.labelsOrAnnotations, {
        [classes.labelsOrAnnotationsTable]: containerType === 'table',
        [classes.labelsOrAnnotationsTableShowAll]: containerType === 'table' && showAll,
      })}
      style={{
        maxWidth: containerType === 'table' ? '480px' : 'max-content',
      }}
      data-testid={generateTestId(entityType)}
    >
      {itemsToShow.map(({ text, tooltipText, additionalText }, index) => (
        <Badge
          variant={variant}
          key={`${text}-${index}`}
          text={text}
          additionalText={additionalText}
          bold={bold}
          ellipsisAt={ellipsisAt}
          hideTooltip={showAll}
          tooltipProps={tooltipListProps}
          tooltipBody={<TooltipListBody items={[tooltipText]} showCopy={false} />}
          className={classes.badge}
          overlayComponent={
            showCopy ? (
              <TooltipOverlay showAll={showAll} text={text} classes={classes} />
            ) : undefined
          }
          data-testid={`badge-${index}`}
        />
      ))}
      {(_maxVisible !== undefined || containerType === 'table') &&
        allItems.length > maxVisible &&
        !(showButton && showAll) && (
          <Badge
            variant={variant}
            text={`+${allItems.length - maxVisible}`}
            bold={bold}
            tooltipBody={
              useTooltipTextInOverflow ? (
                <TooltipListBody
                  items={remainingLabels.map((i) => i.tooltipText ?? i.text)}
                  showCopy={showCopy}
                />
              ) : (
                <TooltipListBody items={remainingLabels} nameKey="text" showCopy={showCopy} />
              )
            }
            tooltipProps={tooltipListProps}
          />
        )}
      {showButton && (
        <Button
          variant="secondary"
          className={classes.showMoreButton}
          onClick={(e) => {
            e?.preventDefault()
            setShowAll(!showAll)
          }}
        >
          <FontAwesomeIcon size="md">{showAll ? 'chevron-up' : 'chevron-down'}</FontAwesomeIcon>
        </Button>
      )}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  labelsOrAnnotations: {
    display: 'flex',
    flexFlow: 'wrap',
    overflow: 'auto',
    gap: 8,
  },
  labelsOrAnnotationsTable: {
    flexFlow: 'nowrap',
  },
  labelsOrAnnotationsTableShowAll: {
    flexFlow: 'wrap',
  },
  showMoreButton: {
    borderRadius: 4,
    padding: '0px 8px',
    minHeight: 'initial',
    height: 26,
  },
  badge: {
    position: 'relative',
    '& > .tooltip-container > .copyToClipboard': {
      display: 'none',
    },
    '&:hover > .tooltip-container > .copyToClipboard': {
      display: 'initial',
    },
  },
  copyToClipboard: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
  tooltipText: {
    color: theme.components.tooltip.text,
    maxWidth: 350,
  },
  copyContainer: {
    cursor: 'pointer',
    backgroundColor: hexToRgbaCss(theme.components.tooltip.copyBackground, 0.8),
    width: 26,
    height: 26,
    boxSizing: 'border-box',
    display: 'grid',
    alignItems: 'center',
    justifyContent: 'center',
    // For some reason border radius isn't matching badge
    // so setting top right radius as 2px
    borderRadius: '0px 2px 4px 0px',
  },
}))
